// Typed client for the platform (super-admin) REST API (P6). Disjoint from tenant
// authorization: these call /api/platform/* and are gated by the caller's PLATFORM
// roles (never tenant membership). Uses the same bearer (session) as the rest of the
// console — reusing ControlPlaneOptions.
import { errorFrom } from './index';
function authHeaders(opts) {
    return { Authorization: `Bearer ${opts.adminToken}` };
}
async function req(opts, method, path, body) {
    const f = opts.fetchImpl ?? fetch;
    const headers = { ...authHeaders(opts) };
    const init = { method, headers };
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }
    const res = await f(new URL(path, opts.baseUrl), init);
    if (!res.ok) {
        throw await errorFrom(res, path);
    }
    return res;
}
async function reqJson(opts, method, path, body) {
    return (await (await req(opts, method, path, body)).json());
}
const enc = encodeURIComponent;
/** The caller's own platform roles (empty ⇒ not a platform operator). */
export function platformWhoami(opts) {
    return reqJson(opts, 'GET', '/api/platform/whoami');
}
// ── Platform role administration (Root Owner) ──────────────────────────────
export function listPlatformRoleAssignments(opts, userId) {
    const q = userId ? `?userId=${enc(userId)}` : '';
    return reqJson(opts, 'GET', `/api/platform/role-assignments${q}`);
}
export function grantPlatformRole(opts, grant) {
    return reqJson(opts, 'POST', '/api/platform/role-assignments', grant);
}
export async function revokePlatformRole(opts, assignmentId) {
    await req(opts, 'DELETE', `/api/platform/role-assignments/${enc(assignmentId)}`);
}
export function platformOverview(opts) {
    return reqJson(opts, 'GET', '/api/platform/overview');
}
// ── Tenant registry + lifecycle ────────────────────────────────────────────
export function listPlatformTenants(opts) {
    return reqJson(opts, 'GET', '/api/platform/tenants');
}
export function provisionTenant(opts, name) {
    return reqJson(opts, 'POST', '/api/platform/tenants', { name });
}
export async function suspendTenant(opts, tenantId) {
    await req(opts, 'POST', `/api/platform/tenants/${enc(tenantId)}/suspend`);
}
export async function reactivatePlatformTenant(opts, tenantId) {
    await req(opts, 'POST', `/api/platform/tenants/${enc(tenantId)}/reactivate`);
}
/** Complete offboarding into the terminal archived state (only from the offboarding state). */
export async function archiveTenant(opts, tenantId) {
    await req(opts, 'POST', `/api/platform/tenants/${enc(tenantId)}/archive`);
}
/** Cancel offboarding during cooling-off, returning the tenant to active. */
export async function cancelTenantOffboarding(opts, tenantId) {
    await req(opts, 'POST', `/api/platform/tenants/${enc(tenantId)}/cancel-offboarding`);
}
/** Place or lift a legal hold, which overrides archiving/deletion. */
export async function setTenantLegalHold(opts, tenantId, hold) {
    await req(opts, 'POST', `/api/platform/tenants/${enc(tenantId)}/legal-hold`, { hold });
}
/** Export a tenant's control-plane data (fleet, config, audit) as an artifact. */
export function exportTenant(opts, tenantId) {
    return reqJson(opts, 'GET', `/api/platform/tenants/${enc(tenantId)}/export`);
}
export function setTenantSubscription(opts, tenantId, planId) {
    return reqJson(opts, 'POST', `/api/platform/tenants/${enc(tenantId)}/subscription`, { planId });
}
// ── Support access (Support requests, Security approves) ───────────────────
export function listSupportGrants(opts) {
    return reqJson(opts, 'GET', '/api/platform/support-grants');
}
export function requestSupportGrant(opts, request) {
    return reqJson(opts, 'POST', '/api/platform/support-grants', request);
}
export async function approveSupportGrant(opts, id) {
    await req(opts, 'POST', `/api/platform/support-grants/${enc(id)}/approve`);
}
export async function rejectSupportGrant(opts, id) {
    await req(opts, 'POST', `/api/platform/support-grants/${enc(id)}/reject`);
}
// ── Tenant offboarding (two-party) ─────────────────────────────────────────
export function listOffboardRequests(opts) {
    return reqJson(opts, 'GET', '/api/platform/offboard-requests');
}
export function requestOffboard(opts, request) {
    return reqJson(opts, 'POST', '/api/platform/offboard-requests', request);
}
export async function approveOffboard(opts, id) {
    await req(opts, 'POST', `/api/platform/offboard-requests/${enc(id)}/approve`);
}
export async function rejectOffboard(opts, id) {
    await req(opts, 'POST', `/api/platform/offboard-requests/${enc(id)}/reject`);
}
// ── Security / audit events ────────────────────────────────────────────────
export function listSecurityEvents(opts, limit = 100) {
    const clamped = Math.max(1, Math.min(500, Math.trunc(limit)));
    return reqJson(opts, 'GET', `/api/platform/security-events?limit=${clamped}`);
}
//# sourceMappingURL=platform.js.map